import Service from '@ember/service';
import { service } from '@ember/service';
import type { IntlService } from 'ember-intl';
import { tracked } from '@glimmer/tracking';

type ValidationErrors = Partial<Record<string, string[]>>;

export default class FormValidationService extends Service {
  @service('intl') declare intlService: IntlService;

  @tracked private rawValidationErrors: ValidationErrors = {};
  @tracked private clearedValidationFields = new Set<string>();

  /**
   * Translates backend validation messages to localized strings
   */
  translateBackendValidationMessage(rawMessage: string): string {
    if (/required/i.test(rawMessage)) {
      return this.intlService.t('validation.required');
    }

    const match = /between\s+(\d+)\s+and\s+(\d+)/i.exec(rawMessage);
    if (match) {
      const minimum = Number(match[1]);
      const maximum = Number(match[2]);
      return this.intlService.t('validation.between', {
        min: minimum,
        max: maximum,
      });
    }

    return rawMessage;
  }

  /**
   * Sets validation errors
   */
  setValidationErrors(errors: ValidationErrors): void {
    this.rawValidationErrors = errors;
  }

  /**
   * Gets filtered validation errors (excluding cleared fields)
   */
  getValidationErrors(): ValidationErrors {
    const allErrors = this.rawValidationErrors;
    if (!allErrors || Object.keys(allErrors).length === 0) return {};

    if (this.clearedValidationFields.size === 0) return allErrors;

    const filteredErrors: ValidationErrors = {};
    for (const [field, messages] of Object.entries(allErrors)) {
      if (messages && !this.clearedValidationFields.has(field)) {
        filteredErrors[field] = messages;
      }
    }
    return filteredErrors;
  }

  /**
   * Gets localized validation errors
   */
  getLocalizedValidationErrors(): ValidationErrors {
    const validationErrors = this.getValidationErrors();
    const localizedErrors: ValidationErrors = {};

    for (const [field, messages] of Object.entries(validationErrors)) {
      if (messages) {
        localizedErrors[field] = messages.map((msg) =>
          this.translateBackendValidationMessage(msg),
        );
      }
    }
    return localizedErrors;
  }

  /**
   * Checks if there are any validation errors
   */
  hasValidationErrors(): boolean {
    return Object.keys(this.getValidationErrors()).length > 0;
  }

  /**
   * Checks if there are any validation errors for a specific field
   */
  hasFieldErrors(fieldName: string): boolean {
    return this.getFieldErrors(fieldName).length > 0;
  }

  /**
   * Gets errors for a specific field
   */
  getFieldErrors(fieldName: string): string[] {
    const localizedErrors = this.getLocalizedValidationErrors();
    return localizedErrors[fieldName] || [];
  }

  /**
   * Clears errors for a specific field
   */
  clearFieldError(fieldName: string): void {
    const fieldSet = new Set(this.clearedValidationFields);
    fieldSet.add(fieldName);
    this.clearedValidationFields = fieldSet;
  }

  /**
   * Clears all errors
   */
  clearAllErrors(): void {
    this.clearedValidationFields = new Set();
    this.rawValidationErrors = {};
  }
}
